import Typography from '@mui/material/Typography';
import React from 'react';
import ExportButton from 'components/ExportButton';
import { ButtonProps } from '@mui/material/Button';
import Box, { BoxProps } from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { translateDateString, translateAsOf } from 'utils/date';
import logo from 'images/MerQube_logos/MerQube_logo(full-color).svg';
import logo2 from 'images/MerQube_logos/Logo-MerQube-Horizontal.svg';
import { useMediaQuery, useTheme } from '@mui/material';

interface Props {
  title: React.ReactNode;
  date?: React.ReactNode;
  value?: React.ReactNode;
  exportProps?: ButtonProps;
  headerProps?: BoxProps;
  displayLogo?: boolean;
}

const CardHeader = ({ title, date, value, exportProps, headerProps, displayLogo }: Props) => {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const dateComponent =
    date &&
    (typeof date === 'string' ? (
      translateDateString(date as string, i18n.language)
    ) : (
      <>
        {translateAsOf(i18n.language)} {date}
      </>
    ));

  const datePickerWrapperProps =
    typeof date === 'string'
      ? {}
      : {
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          position: 'relative',
          top: '9px',
        };

  const longTitle = typeof title === 'string' && (title || '').length > 30;
  const logoSrc = isSmall ? logo2 : logo;

  return (
    <Box className="index-card-header" sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexDirection: {
            md: displayLogo ? 'row-reverse' : 'row',
            sm: displayLogo ? 'row-reverse' : 'row',
            xs: 'column',
          },
        }}
      >
        {displayLogo && (
          <Box
            component="img"
            src={logoSrc}
            alt="MerQube Logo"
            onClick={() => window.open('https://merqube.com', '_blank')}
            sx={{
              height: { xs: '36px', sm: '44px' },
              width: 'auto',
              position: 'relative',
              right: { xs: 0, sm: '12px' },
              // top: '-10px',
              cursor: 'pointer',
              mb: { xs: 1.25, sm: 0 },
            }}
          />
        )}
        <Box sx={{ width: '100%' }}>
          <Box
            display="flex"
            alignItems={longTitle ? 'flex-start' : 'flex-end'}
            marginBottom="-4px"
            flexDirection={longTitle ? 'column' : 'row'}
            flexWrap="wrap"
            justifyContent="flex-start"
            {...headerProps}
          >
            <Typography
              variant="h4"
              color="textPrimary"
              sx={{ margin: 0, paddingRight: '20px', textTransform: 'capitalize' }}
            >
              {title}
            </Typography>
            {value &&
              (typeof value === 'string' ? (
                <Typography variant="h4" color="textPrimary" sx={valueStyles}>
                  {value}
                </Typography>
              ) : (
                <Box sx={{ ...valueStyles, width: '100%' }}>{value}</Box>
              ))}
            {date && (
              <Typography
                variant="h4"
                color="textPrimary"
                sx={{
                  fontSize: '12px',
                  lineHeight: '18px',
                  letterSpacing: '2px',
                  opacity: 0.5,
                  fontWeight: 400,
                  textTransform: 'uppercase',
                  margin: 0,
                  paddingRight: '6px',
                  ...datePickerWrapperProps,
                }}
              >
                {dateComponent}
              </Typography>
            )}
            {exportProps && <ExportButton {...exportProps} />}
          </Box>
          <div className="mini-separator" style={{ marginBottom: 0 }} />
        </Box>
      </Box>
    </Box>
  );
};

export default CardHeader;

const valueStyles = {
  fontSize: '12px',
  lineHeight: '18px',
  letterSpacing: '2px',
  opacity: 0.5,
  margin: 0,
  paddingRight: '6px',
  fontWeight: 700,
};
